import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import { Checkbox } from '@art-of-hospitality-solutions-llc/ui-kit/components/checkbox';
import { Label } from '@art-of-hospitality-solutions-llc/ui-kit/components/label';
import { ColumnConfig } from '@art-of-hospitality-solutions-llc/ui-kit/components/data-table';
import {
  toastError,
  toastSuccess,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import { ResourceTableView } from '@Components/ResourceTable';
import { useResourceTableController } from '@Components/ResourceTable/useResourceTableController';
import ConfirmationDialog from '@Components/ConfirmationDialog';
import type { AssetTag } from '@Types/assets';
import { buildFilterParams } from '@Utils/buildFilterParams';
import { useAssetTags } from '@Hooks/useAssetTags';
import { useDeleteAssetTag } from '@Hooks/useDeleteAssetTag';
import { Tag, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

const ASSET_TAG_FIELD_MAPPING: Record<string, string> = {
  name: 'filter[name]',
};

const assetTagColumnConfigs: ColumnConfig<AssetTag>[] = [
  {
    id: 'name',
    headerLabel: 'Name',
    accessor: (row) => row.name,
    type: 'text',
    size: 300,
  },
  {
    id: 'assets',
    headerLabel: 'Assets',
    accessor: (row) => row.assets?.length ?? 0,
    type: 'number',
    size: 100,
  },
  {
    id: 'createdAt',
    headerLabel: 'Created',
    accessor: (row) =>
      new Date(row.createdAt).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      }),
    type: 'text',
    size: 150,
  },
];

export default function AssetTagList() {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [tagToDelete, setTagToDelete] = useState<AssetTag | null>(null);
  const [showUnassociatedOnly, setShowUnassociatedOnly] = useState(false);

  const deleteMutation = useDeleteAssetTag();

  const handleDeleteClick = (tag: AssetTag) => {
    setTagToDelete(tag);
    setDeleteDialogOpen(true);
  };

  const controller = useResourceTableController<AssetTag>({
    columnsConfig: assetTagColumnConfigs,
    defaultSort: {
      id: 'name',
      desc: false,
    },
    buildFilterParams: (filters) =>
      buildFilterParams(filters, {
        fieldMapping: ASSET_TAG_FIELD_MAPPING,
      }),
    quickFilterFields: [{ field: 'name', label: 'Name' }],
    initialPageSize: 10,
    rowActions: (row) => (
      <div className="flex gap-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            handleDeleteClick(row);
          }}
        >
          <Trash2 className="h-4 w-4 text-red-500" />
        </Button>
      </div>
    ),
  });

  const {
    data: assetTagsData,
    status,
    isError,
    error,
  } = useAssetTags({ searchParams: controller.searchParams });

  useEffect(() => {
    if (isError && error) {
      toastError(
        error instanceof Error ? error.message : 'Failed to load asset tags',
      );
    }
  }, [isError, error]);

  const allRows: AssetTag[] = assetTagsData?.data ?? [];
  const meta = assetTagsData?.meta;

  const rows = useMemo(
    () =>
      showUnassociatedOnly
        ? allRows.filter((tag) => !tag.assets || tag.assets.length === 0)
        : allRows,
    [allRows, showUnassociatedOnly],
  );

  const handleDeleteConfirm = async () => {
    if (!tagToDelete) return;

    try {
      await deleteMutation.mutateAsync({ id: tagToDelete.id });
      toastSuccess(`"${tagToDelete.name}" has been deleted.`);
      setDeleteDialogOpen(false);
      setTagToDelete(null);
    } catch (err) {
      toastError(
        err instanceof Error ? err.message : 'Failed to delete asset tag',
      );
    }
  };

  return (
    <>
      <ResourceTableView<AssetTag>
        label="Asset Tags"
        controller={controller}
        data={rows}
        meta={meta}
        status={status}
        summaryIcon={<Tag className="h-4 w-4 text-zinc-500" />}
        summaryLabel="Total Asset Tags: "
        renderQuickFiltersExtra={() => (
          <div className="flex items-center gap-2">
            <Checkbox
              id="show-unassociated"
              checked={showUnassociatedOnly}
              onCheckedChange={(checked) =>
                setShowUnassociatedOnly(checked === true)
              }
            />
            <Label htmlFor="show-unassociated">Unassociated only</Label>
          </div>
        )}
      />

      <ConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onSubmit={handleDeleteConfirm}
        match={tagToDelete?.name ?? ''}
        title="Delete Asset Tag"
        description={`Are you sure you want to delete "${tagToDelete?.name}"? This action cannot be undone.`}
        matchLabel="Type the tag name to confirm"
        submitLabel="Delete"
        isLoading={deleteMutation.isPending}
        variant="destructive"
      />
    </>
  );
}
